import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { AgeBadge } from "@/components/AgeBadge";
import { formatEUR } from "@/lib/format";
import { cn } from "@/lib/utils";
import { DemoFrame, Segmented } from "./DemoFrame";
import {
  DEMO_CLIENTS,
  sortWorklist,
  toRelaunch,
  type DemoClient,
  type WorklistOrder,
} from "./demoData";

const ORDERS: ReadonlyArray<{ value: WorklistOrder; label: string }> = [
  { value: "jour", label: "Ordre du jour" },
  { value: "montant", label: "Montant" },
  { value: "anciennete", label: "Ancienneté" },
];

function Tag({ client }: { client: DemoClient }) {
  const tag = client.dueToday
    ? { text: "Action prévue aujourd’hui", cls: "bg-action-soft text-action-strong" }
    : client.neglected
      ? { text: "Négligé", cls: "bg-warning-soft text-warning-strong" }
      : null;
  if (!tag) return null;
  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2 py-0.5 text-2xs font-semibold whitespace-nowrap",
        tag.cls,
      )}
    >
      {tag.text}
    </span>
  );
}

/**
 * La liste « À relancer » de l'app, en miniature : même ordre (relance du
 * jour, client oublié, puis plus ancien retard) et mêmes badges. Les tris
 * réordonnent les lignes en les faisant glisser.
 */
export function PriorityDemo() {
  const [order, setOrder] = useState<WorklistOrder>("jour");
  const reduce = useReducedMotion();
  const rows = sortWorklist(toRelaunch(DEMO_CLIENTS), order);

  return (
    <DemoFrame
      title={
        <>
          À relancer{" "}
          <span className="font-normal text-muted-foreground">
            · {rows.length} clients
          </span>
        </>
      }
      aside={
        <Segmented
          label="Trier la liste"
          options={ORDERS}
          value={order}
          onChange={setOrder}
        />
      }
      footnote="Données fictives · l’ordre du jour est celui de la liste « À relancer » de l’app"
    >
      <ul aria-label="Clients à relancer" className="divide-y divide-border">
        {rows.map((client) => (
          <motion.li
            key={client.id}
            data-client={client.id}
            layout={!reduce}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1 py-3 sm:grid-cols-[1fr_auto_auto]"
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="truncate text-sm font-semibold text-foreground">
                  {client.name}
                </span>
                <Tag client={client} />
              </div>
              <p className="mt-0.5 truncate text-caption text-muted-foreground">
                {client.note}
              </p>
            </div>
            <span className="hidden sm:block">
              <AgeBadge days={client.days} />
            </span>
            <span className="text-right text-sm font-semibold text-foreground">
              {formatEUR(client.amount)}
            </span>
          </motion.li>
        ))}
      </ul>
    </DemoFrame>
  );
}
